import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import "./styles.css";
import { Donut } from "../Graphs";
import { RippleButton, WidgetLegend } from "../../Atoms";
import { ReactTable } from "../../Molecules";
import { ROUTES_SUMMARY_HEADERS } from "../../../Library/tableHeaders";
import { secondsToHms } from "../../../Library/datetime";
import { AddIcon, DownloadIcon } from "../../../Assets/images/svgs";
import { Link, useNavigate } from "react-router-dom";
import PATHS from "../../../Routes/paths";
import { getRouteStats, getDrivers } from "../../../APIs";
import { StoreContext } from "../../../Library/contexts";
import { DATE_RANGES } from "../../../Library/constants";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";
import { downloadReport, transformAPIDataToReport } from "../../../Library/misReportExcel";
import { Button, Image, Spinner } from "@chakra-ui/react";
import { NoRoutes, NoRouteStats } from "../../../Assets/images/pngs";

const RouteSummary = ({ pieData = [], allRoutes = [] }) => {
  const [ graphData, setGraphData ] = useState(pieData);
  const [ cardsData, setCardsData ] = useState([]);
  const [ routeData, setRouteData ] = useState([]);
  const [ tableData, setTableData ] = useState([]);
  const [ showLoader, setShowLoader ] = useState(true);

  const { userVehicles, filters, drivers, allVehicleRoutes } = useContext(StoreContext);
  const [reportDownloading, setReportDownloading] = useState(false);
  const selectedDateDataMap = useRef({});
  const navigate = useNavigate();

  const COLORS = {
    GREEN: "rgba(81, 180, 65, 1)",
    RED: "rgba(226, 85, 85, 1)",
    ORANGE: "rgba(253, 160, 20, 1)",
  };

  const processFetchedStats = (routeStats) => {
    let expectedRouteDst = 0; // metres
    let actualRouteDst = 0;   // metres
    let deviatedDst = 0;      // metres
    let totalDistance = 0;    // metres

    routeStats.forEach(routeStat => {
      totalDistance += routeStat.dayStats.dst || 0;
      routeStat.routes.forEach(route => {
        // routeDst is the per-trip route distance (metres); multiply by trips for total expected
        expectedRouteDst += (route.routeDst || 0) * (route.trips || 0);
        actualRouteDst += route.dst || 0;
        deviatedDst += route.deviated || 0;
      });
    });

    const expectedRouteDstKm = expectedRouteDst / 1000;

    setGraphData([{ count: expectedRouteDstKm.toFixed(1), label: "Expected Distance", color: COLORS.GREEN }, { count: (deviatedDst/1000).toFixed(1), label: "Deviated Distance", color: COLORS.RED }, { count: ((totalDistance - actualRouteDst)/1000).toFixed(1), label: "Other Travel", color: COLORS.ORANGE }]);

    setRouteData(routeStats);
    selectedDateDataMap.current[filters.range] = routeStats;
    setCardsData([
      { label: "Expected Route Distance", value: expectedRouteDstKm },
      { label: "Actual Route Distance", value: (actualRouteDst/1000) },
      { label: "Deviated Distance", value: (deviatedDst/1000), deviated: true },
      // Total Distance = sum of actual GPS distance on route trips (same pool as actualRouteDst).
      // dayStats.dst (totalDistance) includes driving before/after the trip window which inflates it.
      { label: "Total Distance", value: (actualRouteDst/1000) },
    ]);
  }

  useEffect(() => {
    const uniqueVids = [...new Set(allRoutes.flatMap(item => item.vids))];
    if (!uniqueVids || !uniqueVids.length) return setShowLoader(false);

    let selectedDate = DATE_RANGES[filters.range];

    if (selectedDateDataMap.current[filters.range] && selectedDateDataMap.current[filters.range].length) return processFetchedStats(selectedDateDataMap.current[filters.range]);

    getRouteStats({ fromTs: selectedDate.from, toTs: selectedDate.to, vids: uniqueVids }, (err, routeStats) => {
      if (err) console.log(err);
      else {
        const activeRouteIdxSet = new Set(allRoutes.map(route => route.idx));
        const filteredRouteStats = routeStats
          .map(stat => {
            const filteredRoutes = stat.routes.filter(r => activeRouteIdxSet.has(r.routeId));

            return {
              ...stat,
              routes: filteredRoutes,
            };
          })
          .filter(stat => stat.routes.length > 0);

        processFetchedStats(filteredRouteStats);
      }

      setShowLoader(false);
    }, []);
  }, [ allRoutes, filters.range ]);

  useEffect(() => {
    if (!userVehicles || !userVehicles.length || !routeData || !routeData.length) return;

    let vehicleMap = {};
    userVehicles.forEach(veh => {
      vehicleMap[veh.idx] = veh;
    });

    const mergedDataMap = new Map();

    routeData.forEach(route => {
      const { vid, dayStats } = route;
      const regNo = vehicleMap[vid]?.reg;
      const totalRoutes = allRoutes.filter(rt => rt.vids.includes(vid));

      if (!mergedDataMap.has(vid)) {
        mergedDataMap.set(vid, {
          vid,
          regNo,
          uniqueRoutesTravelled: new Set(),
          totalTripCount: 0,
          totalExpectedDist: 0,
          totalActualDist: 0,
          totalDeviatedDist: 0,
          totalTravelledDist: 0,
          totalTravelledDur: 0,
        });
      }

      const merged = mergedDataMap.get(vid);
      merged.totalTravelledDist += dayStats.dst || 0;

      route.routes.forEach((rRoute) => {
        let { routeId, trips, routeDst, dst, dur, deviated } = rRoute;

        // routeDst is the per-trip route distance (metres); accumulate per trip across all days
        merged.totalExpectedDist += (routeDst || 0) * (trips || 0);
        merged.uniqueRoutesTravelled.add(routeId);
        merged.totalTripCount += trips;
        merged.totalActualDist += dst;
        merged.totalDeviatedDist += deviated;
        merged.totalTravelledDur += dur;
      });

      merged.routeCount = `${merged.uniqueRoutesTravelled.size}/${totalRoutes.length}`;
    });

    const processedData = Array.from(mergedDataMap.values()).map(item => ({
      vid: item.vid,
      regNo: item.regNo,
      routeCount: item.routeCount,
      tripCount: item.totalTripCount,
      totalExpectedDist: (item.totalExpectedDist/1000).toFixed(1),
      totalActualDist: (item.totalActualDist/1000).toFixed(1),
      totalDeviatedDist: (item.totalDeviatedDist/1000).toFixed(1),
      totalTravelledDist: (item.totalActualDist/1000).toFixed(1),
      totalTravelledDur: item.totalTravelledDur,
    }));

    setTableData(processedData);
  }, [ routeData, userVehicles ]);

  const parser = { x: "label", y: "count" };
  const columns = useMemo(() => Object.values(ROUTES_SUMMARY_HEADERS).map((header) => {
    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => {
        if (row.column.id === "regNo") {
          return <span className="route-vehicle-reg-link">
            <Link to={`${PATHS.VIEW_ROUTES}${PATHS.ROUTES_SUMMARY}?reg=${row.value}&idx=${row.row.original.vid}`}>{row.value}</Link>
          </span>
        }

        if (row.column.id === "routeCount") {
          return <span>
            {row.value}
          </span>
        }

        if (row.column.id === "totalExpectedDist" || row.column.id === "totalActualDist" || row.column.id === "totalDeviatedDist" || row.column.id === "totalTravelledDist") {
          return <span>
            {row.value} kms
          </span>
        }

        if (row.column.id === "totalTravelledDur") {
          if (!row.value) return "-"
          let hms = secondsToHms(row.value, true);
          return <span>
            {`${hms.h}h ${hms.m}m`}
          </span>
        }

        return (
          <span>
            {row.value}
          </span>
        )
      },
    };
  }), []);

  /*const handleReportDownload = () => {
    var sendToExcel = tableData.map(a => Object.assign({}, a));
    sendToExcel.forEach((stat, idx) => {
      stat["Sr No"] = idx + 1;
      stat["Registration Number"] = stat.regNo || "-";
      stat["Routes Travelled"] = stat.routeCount || "-";
      stat["Trips"] = stat.tripCount || "-";
      stat["Expected Route Distance"] = stat.totalExpectedDist ? `${stat.totalExpectedDist} kms` : "-";
      stat["Actual Route Distance"] = stat.totalActualDist ? `${stat.totalActualDist} kms` : "-";
      stat["Deviated Distance"] = stat.totalDeviatedDist ? `${stat.totalDeviatedDist} kms` : "-";
      stat["Total Distance"] = stat.totalTravelledDist ? `${stat.totalTravelledDist} kms` : "-";
      stat["Total Duration"] = stat.totalTravelledDur ? secondsToHms(stat.totalTravelledDur) : "-";
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Registration Number, Routes Travelled, Trips, Expected Route Distance, Actual Route Distance, Deviated Distance, Total Distance, Total Duration";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Routes_" + Date.now(), false);
  }*/

  const handleReportDownload = async () => {
    if (!routeData || !routeData.length) return;
    setReportDownloading(true);
    try {
      // drivers in StoreContext is only populated after visiting DriverManagement.
      // Fetch on-demand if empty so driver names always resolve correctly.
      let resolvedDrivers = drivers && drivers.length ? drivers : [];
      if (!resolvedDrivers.length) {
        await new Promise(resolve => {
          getDrivers((err, data) => {
            if (err) console.error('[Report] Failed to fetch drivers:', err);
            else if (!data) console.warn('[Report] getDrivers returned empty data');
            else resolvedDrivers = data;
            resolve();
          });
        });
      }

      const reportData = transformAPIDataToReport(
        routeData,
        userVehicles,
        allVehicleRoutes || allRoutes,
        resolvedDrivers,
        filters.range
      );
      await downloadReport(reportData);
    } catch (e) {
      console.error('Report download failed', e);
    } finally {
      setReportDownloading(false);
    }
  };

  return (
    <div className="panel-main route-summary">
      {!showLoader ? <>
        {(allRoutes.length && routeData.length) ? <div className="summary-cards-container">
          <div className="routes-graph-and-stats-container">
            <div className="routes-graph-container">
              <div className="widget-chart">
                <Donut cutout={55} tooltipPosition="cursor" title="Vehicles" data={graphData} parser={parser} />
              </div>
              <div className="route-chart-labels">
                <WidgetLegend ellipsify={false} showCount={false} showPercent={false} doSort={false} data={graphData} parser={parser} />
              </div>
            </div>
            <div className="routes-stats-container">
              {cardsData.map(data => (
                <div className="route-point-card">
                  <p className={`route-stat-value ${data.deviated ? "highlighted-text" : ""}`}>{`${data.value ? (Number(data.value)).toFixed(1) : "-"} kms`}</p>
                  <p className="route-stat-label">{data.label || "-"}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="routes-travelled-table-container">
            <div className="routes-travelled-table-header">
              {/* <RippleButton onClick={() => handleReportDownload()} className="routes-report-download-btn">
                <DownloadIcon />
              </RippleButton> */}
              <RippleButton onClick={handleReportDownload} disable={reportDownloading} className="routes-report-download-btn">
                {reportDownloading ? <Spinner size="xs" /> : <DownloadIcon />}
              </RippleButton>
            </div>
            <ReactTable
              data={tableData}
              columns={columns}
              pageSize={5}
            />
          </div>
        </div> : <div className="create-route-illustration">
          <Image src={allRoutes.length ? NoRouteStats : NoRoutes} />
          <p>{allRoutes.length ? "Route summary will appear here once vehicles start moving on the assigned routes" : "Create your first route to start detecting deviations"}</p>
          {!allRoutes.length && <Button className="set-route-btn" onClick={() => navigate(`${PATHS.VIEW_ROUTES}${PATHS.SET_ROUTES}?fromAlerts=${true}`)}>
            <AddIcon />
            Create new route
          </Button>}
        </div>}
      </> : <Spinner size={"lg"}  className="route-summary-loader" />}
    </div>
  )
}

export default RouteSummary
